/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/react";
import React from "react";
import QRCode from "qrcode.react";
import Box from "@material-ui/core/Box";
import { useI18n } from "hooks/i18n/i18n";
import { IssuedTicket } from "@spwn/types/firebase/firestore";
import { makeEncodedIssuedTicketQRCodeValue } from "utility/ticket";
import { getSlashSeparatedDateTimeStringFromFbTimestamp } from "utility";
import { ActionModalType } from "modules/modal";
import { AccountTicketDigitalQrCodeSharingArea } from "./AccountTicketDigitalQrCodeSharingArea";
import { AccountTicketDigitalHoldToCheckInButton } from "./AccountTicketDigitalHoldToCheckInButton";
import { useAccountTicketDigitalQrCode } from "./useAccountTicketDigitalQrCode";
import { useQRCodeSize } from "./useQRCodeSize";

export const AccountTicketDigitalQrCode: React.FC<{
  userId: string;
  eventId: string | null;
  relatedTicket: IssuedTicket;
  openActionModal: (
    modalType: ActionModalType,
    ticketNum: string,
    issuedTicket: IssuedTicket | null
  ) => void;
  onEventCheckIn: (
    ticketNum: string,
    issuedTicket: IssuedTicket | null
  ) => void;
  onRewardCheckIn: (
    ticketNum: string,
    issuedTicket: IssuedTicket | null
  ) => void;
  ticket: {
    isVodTicket: boolean;
    isChecked: boolean;
    isTicketSharable: boolean;
    ticketId: string;
  };
  benefits: {
    productClass: number | undefined;
    isRedeemed: boolean;
  };
}> = ({
  userId,
  eventId,
  relatedTicket,
  openActionModal,
  onEventCheckIn,
  onRewardCheckIn,
  ticket,
  benefits,
}) => {
  const { t } = useI18n();
  const { shareStatus, ticketShareMessage, isShared, isBenefitGoods } =
    useAccountTicketDigitalQrCode({
      userId,
      relatedTicket,
      benefits,
    });

  const [selectedTabNumber, setSelectedTabNumber] = React.useState<number>(0);
  const { containerRef, qrCodeSize } = useQRCodeSize({
    maxSize: 224,
    minSize: 120,
    padding: 56, // 28px * 2
  });

  const handleChangeIndex = (index: number) => {
    setSelectedTabNumber(index);
  };

  if (!eventId) {
    throw new Error(`eventId is required`);
  }
  if (!relatedTicket) {
    throw new Error(`eventId: ${eventId}, relatedTicket is required`);
  }

  /**
   * @description チケット番号と認証コードを取得する
   * @todo 本来は型を使って取得するべきだが、受け取る関数との型が合わないためanyにキャストしている
   */
  const { ticketNum, authCode } = relatedTicket as {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    ticketNum: any;
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    authCode: any;
  };

  /**
   * @description チケット使用・特典引き換え用のQRコードを作成する
   */
  const createQRCode = (type: "ticket" | "benefits") => {
    /**
     * @description
     * isChecked
     *  - チケットのチェックインしたかどうか
     * isRedeemed
     *  - 特典の引換をしたかどうか
     */
    const ticketType = type === "ticket" ? "isChecked" : "isRedeemed";

    return makeEncodedIssuedTicketQRCodeValue({
      eventId,
      ticketNumber: ticketNum,
      authCode: authCode || null,
      ticketType,
      ticketId: ticket.ticketId,
    });
  };

  const handleEventCheckIn = () => {
    onEventCheckIn(ticketNum, relatedTicket);
  };

  const handleRewardCheckIn = () => {
    onRewardCheckIn(ticketNum, relatedTicket);
  };

  const handleOnClickShareTicket = () => {
    if (shareStatus === "beforeSharing" || shareStatus === "createShareUrl") {
      openActionModal("shareTicket", ticketNum, relatedTicket);
    }
    return;
  };

  // args引き換え処理に渡すargs
  // const rewardCheckInArgs: ReqCollectTicket = {
  //   // @ts-expect-error TS2322
  //   eid: eventId,
  //   ticketNumber: ticketNum,
  //   authCode: issuedTicket.authCode,
  // };

  // 特典引き換え処理
  // dispatch(ticketActions.redeemTicket.started(rewardCheckInArgs))

  // チケット引換処理
  // dispatch(ticketActions.collectTicket.started(rewardCheckInArgs));

  const tabSelectedColor = "white";
  const tabNotSelectedColor = "rgba(0, 0, 0, 0.16)";

  const tabNotSelectedFontColor = "white";
  const tabSelectedAndNotRedeemedFontColor = "black";
  const tabSelectedAndRedeemedFontColor = "#A1A1A1";

  const changeTabColor = ({ isSelected }: { isSelected: boolean }) => {
    if (isSelected) return tabSelectedColor;
    return tabNotSelectedColor;
  };

  const changeTabFontColor = ({
    isSelected,
    isRedeemed,
  }: {
    isSelected: boolean;
    isRedeemed: boolean;
  }) => {
    if (!isSelected) return tabNotSelectedFontColor;
    if (isRedeemed) return tabSelectedAndRedeemedFontColor;
    return tabSelectedAndNotRedeemedFontColor;
  };

  return (
    <div ref={containerRef}>
      <Box maxWidth={"500px"} margin={"0 auto"}>
        {/* チケットと特典のQRを切り替えるタブ */}
        {isBenefitGoods && (
          <Box
            component={"ul"}
            display={"grid"}
            gridTemplateColumns={"1fr 1fr"}
            gridGap={"8px"}
            marginBottom={"12px"}
          >
            <Box
              component={"li"}
              padding={"14px"}
              color={changeTabFontColor({
                isSelected: selectedTabNumber === 0,
                isRedeemed: ticket.isChecked,
              })}
              fontWeight={"bold"}
              textAlign={"center"}
              bgcolor={changeTabColor({ isSelected: selectedTabNumber === 0 })}
              style={{ cursor: "pointer" }}
              onClick={() => handleChangeIndex(0)}
            >
              {t("ticketDetail.ticket.subTitle")}
            </Box>
            <Box
              component={"li"}
              padding={"14px"}
              color={changeTabFontColor({
                isSelected: selectedTabNumber === 1,
                isRedeemed: benefits.isRedeemed,
              })}
              fontWeight={"bold"}
              textAlign={"center"}
              bgcolor={changeTabColor({ isSelected: selectedTabNumber === 1 })}
              style={{ cursor: "pointer" }}
              onClick={() => handleChangeIndex(1)}
            >
              {t("ticketDetail.benefits")}
            </Box>
          </Box>
        )}

        {/* 選択したタブの項目に表示を切り替える */}
        {selectedTabNumber === 0 && (
          <Box>
            {!ticket.isVodTicket && (
              // チケットの種類がEvent Ticketの場合、QRコードを表示する
              <React.Fragment>
                {shareStatus === "shared" ? (
                  <Box
                    component={"p"}
                    padding={"28px"}
                    marginBottom={"20px"}
                    textAlign={"center"}
                    bgcolor={"white"}
                    color={"#222"}
                    fontWeight={"bold"}
                  >
                    {t("ticketDetail.cannotUseDueToShared")}
                  </Box>
                ) : (
                  <React.Fragment>
                    <Box
                      component={"div"}
                      padding={"28px"}
                      textAlign={"center"}
                      bgcolor={"white"}
                    >
                      <Box display={"inline-block"}>
                        <QRCode
                          value={createQRCode("ticket")}
                          size={qrCodeSize}
                          style={{
                            display: "block",
                          }}
                        />
                      </Box>
                    </Box>
                    <Box
                      marginTop={"12px"}
                      marginBottom={"20px"}
                      fontSize={"12px"}
                      color={"#fff"}
                      textAlign={"center"}
                      lineHeight={1.4}
                    >
                      {t("ticketDetail.brightenScreenForQrcode")}
                    </Box>
                    <AccountTicketDigitalHoldToCheckInButton
                      isChecked={ticket.isChecked}
                      checkedLabel={t("ticketDetail.checkedIn")}
                      idleLabel={t("ticketDetail.checkInOnlyStaff")}
                      holdingLabel={t("ticketDetail.confirmingHold")}
                      checkedAtLabel={
                        relatedTicket.checkedDateTime
                          ? t("ticketDetail.checkedInDate", {
                              date: getSlashSeparatedDateTimeStringFromFbTimestamp(
                                relatedTicket.checkedDateTime
                              ),
                            })
                          : undefined
                      }
                      onComplete={handleEventCheckIn}
                    />
                  </React.Fragment>
                )}
                {/* チケットの共有機能が有効の時に表示する */}
                {ticket.isTicketSharable && (
                  <AccountTicketDigitalQrCodeSharingArea
                    isShared={isShared}
                    ticketShareMessage={ticketShareMessage}
                    ticketShareButtonLabel={
                      shareStatus === "beforeSharing"
                        ? t("ticketDetail.shareThisTicket")
                        : shareStatus === "createShareUrl"
                          ? t("ticketDetail.showShareUrl")
                          : t("ticketDetail.shared")
                    }
                    onClickTicketShareButton={handleOnClickShareTicket}
                  />
                )}
              </React.Fragment>
            )}
          </Box>
        )}

        {selectedTabNumber === 1 && isBenefitGoods && (
          <Box>
            {shareStatus === "shared" ? (
              <Box
                component={"p"}
                padding={"28px"}
                marginBottom={"20px"}
                textAlign={"center"}
                bgcolor={"white"}
                color={"#222"}
                fontWeight={"bold"}
              >
                {t("ticketDetail.cannotUseDueToShared")}
              </Box>
            ) : (
              <React.Fragment>
                <Box
                  component={"div"}
                  padding={"28px"}
                  textAlign={"center"}
                  bgcolor={"white"}
                >
                  <Box display={"inline-block"}>
                    <QRCode
                      value={createQRCode("benefits")}
                      size={qrCodeSize}
                      style={{
                        display: "block",
                      }}
                    />
                  </Box>
                </Box>
                <Box
                  marginTop={"12px"}
                  marginBottom={"20px"}
                  fontSize={"12px"}
                  color={"#fff"}
                  textAlign={"center"}
                  lineHeight={1.4}
                >
                  {t("ticketDetail.brightenScreenForQrcode")}
                </Box>

                <AccountTicketDigitalHoldToCheckInButton
                  isChecked={benefits.isRedeemed}
                  checkedLabel={t("ticketDetail.benefitsRedeemed")}
                  idleLabel={t("ticketDetail.benefitsRedeemOnlyStaff")}
                  holdingLabel={t("ticketDetail.confirmingBenefitsRedeem")}
                  checkedAtLabel={
                    relatedTicket.redeemedDateTime
                      ? t("ticketDetail.benefitsRedeemedDate", {
                          date: getSlashSeparatedDateTimeStringFromFbTimestamp(
                            relatedTicket.redeemedDateTime
                          ),
                        })
                      : undefined
                  }
                  onComplete={handleRewardCheckIn}
                />
              </React.Fragment>
            )}
          </Box>
        )}
      </Box>
    </div>
  );
};
